/* =========================================================================
   SAMSARA — SECTION-PRODUCT-HERO.JS
   Sprint 3 / T1 — Gallery thumb swap + variant selector live + quantity.

   Modules:
     1. Gallery thumb swap — click thumb → swap primary cu fade
     2. Variant selector — radio change → update hidden id + price
     3. Quantity buttons — increment/decrement cu min:1 max:99 clamp

   Vanilla, zero deps. CLAUDE.md JS conventions §3.
   ========================================================================= */

(function () {
  'use strict';

  const heroes = document.querySelectorAll('[data-product-hero]');
  if (!heroes.length) return;

  heroes.forEach(initHero);

  function initHero(hero) {
    initGallery(hero);
    initVariantSelector(hero);
    initQuantity(hero);
    initStickyBuy(hero);
    initPlanFrequency(hero);
  }

  /* -----------------------------------------------------------------
     PLAN FREQUENCY — dropdown sync. When user picks a frequency from
     the Abonament card dropdown, update the subscribe radio value to
     match the selected plan id, auto-check Abonament, and refresh price.
     ----------------------------------------------------------------- */
  function initPlanFrequency(hero) {
    const select = hero.querySelector('[data-plan-frequency]');
    const subscribeRadio = hero.querySelector('[data-plan-subscribe]');
    const priceDisplay = hero.querySelector('[data-plan-price-display]');
    if (!select || !subscribeRadio) return;

    select.addEventListener('change', function (e) {
      const planId = select.value;
      const selectedOpt = select.options[select.selectedIndex];
      subscribeRadio.value = planId;
      subscribeRadio.checked = true;
      subscribeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      if (priceDisplay && selectedOpt && selectedOpt.dataset.planPrice) {
        priceDisplay.textContent = formatMoney(parseInt(selectedOpt.dataset.planPrice, 10));
      }
    });

    select.addEventListener('click', function (e) { e.stopPropagation(); });
    select.addEventListener('mousedown', function (e) { e.stopPropagation(); });
  }

  function formatMoney(cents) {
    const lei = cents / 100;
    if (lei === Math.floor(lei)) {
      return lei + ' lei';
    }
    return lei.toFixed(2).replace('.', ',') + ' lei';
  }


  /* -----------------------------------------------------------------
     GALLERY — thumb click swaps primary image
     ----------------------------------------------------------------- */
  function initGallery(hero) {
    const slots = hero.querySelectorAll('[data-product-gallery-slot]');
    const thumbs = hero.querySelectorAll('[data-product-gallery-thumb]');
    if (!slots.length || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const targetPos = thumb.dataset.position;

        slots.forEach(function (slot) {
          const isTarget = slot.dataset.position === targetPos;
          slot.classList.toggle('is-active', isTarget);
          slot.setAttribute('aria-hidden', String(!isTarget));
        });

        thumbs.forEach(function (t) {
          const isTarget = t.dataset.position === targetPos;
          t.classList.toggle('is-active', isTarget);
          t.setAttribute('aria-selected', String(isTarget));
        });
      });
    });
  }

  /* -----------------------------------------------------------------
     VARIANT SELECTOR — radio change → match variant.id, update form + price
     ----------------------------------------------------------------- */
  function initVariantSelector(hero) {
    const form = hero.querySelector('[data-product-form]');
    if (!form) return;

    const variantIdInput = form.querySelector('[data-product-variant-id]');
    const optionInputs = form.querySelectorAll('[data-option-input]');
    const ctaBtn = form.querySelector('[data-product-add-cta]');
    const productHandle = hero.dataset.productHandle;

    if (!variantIdInput || !optionInputs.length || !productHandle) return;

    // Product JSON cache (un singur fetch / pagină) + token monotonic.
    // Elimină fetch-storm-ul (perf) ȘI race-ul: ultimul toggle câștigă mereu,
    // fără AbortController (care otrăvea promise-ul shared pe cold-cache).
    var productPromise = null;
    var reqSeq = 0;
    function getProduct() {
      if (!productPromise) {
        productPromise = fetch('/products/' + productHandle + '.js')
          .then(function (r) {
            if (!r.ok) throw new Error('product ' + r.status);
            return r.json();
          })
          .catch(function (e) {
            productPromise = null; // permite retry la următorul toggle
            throw e;
          });
      }
      return productPromise;
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', updateVariant);
    });

    function getSelectedOptions() {
      const selected = {};
      optionInputs.forEach(function (input) {
        // <select> elements (Măcinare dropdown) — read value directly.
        // <input type="radio"> elements (Gramaj pills) — read only if checked.
        const isSelect = input.tagName === 'SELECT';
        if (isSelect || input.checked) {
          const position = input.name.replace('option-', '');
          selected[position] = input.value;
        }
      });
      return selected;
    }

    function updateVariant() {
      // Token monotonic: dacă un toggle mai nou pornește înainte ca acesta să se
      // rezolve, cel vechi se anulează singur (myReq !== reqSeq). Garantează că
      // ultima selecție câștigă, indiferent de ordinea de revenire a promise-ului.
      var myReq = ++reqSeq;

      getProduct()
        .then(function (product) {
          if (myReq !== reqSeq) return; // un toggle mai nou m-a depășit

          // Re-citim selecția AICI, după resolve — starea curentă din DOM.
          const selected = getSelectedOptions();
          const optionValues = Object.keys(selected)
            .sort()
            .map(function (k) { return selected[k]; });

          const match = product.variants.find(function (v) {
            return v.options.every(function (opt, idx) {
              return opt === optionValues[idx];
            });
          });

          if (!match) {
            /* Combinatie inexistenta (ex. 1000g + macinare, cand 1 kg e doar boabe).
               Inainte pastram ultimul ID valid si butonul adauga in cos varianta veche
               de 250g la 57 lei, desi clientul alesese 1 kg (audit 2026-09-10). */
            variantIdInput.value = '';
            updateAvailability({ available: false });
            return;
          }

          variantIdInput.value = match.id;
          updateAvailability(match);
          updatePriceVisibility(match);
          updatePlanPrices(match);
          dispatchVariantChange(match);
        })
        .catch(function () {
          /* Nu putem confirma ca selectia din DOM corespunde variantei din formular,
             deci blocam cumpararea in loc sa trimitem ultimul ID cunoscut. Cache-ul
             se reseteaza la eroare, deci urmatorul toggle reincearca fetch-ul. */
          if (myReq !== reqSeq) return;
          variantIdInput.value = '';
          updateAvailability({ available: false });
        });
    }

    function updatePriceVisibility(variant) {
      // Pre-rendered per-variant price blocks: show only matched, hide rest.
      const priceBlocks = hero.querySelectorAll('[data-variant-price-id]');
      priceBlocks.forEach(function (block) {
        const match = String(block.dataset.variantPriceId) === String(variant.id);
        if (match) {
          block.removeAttribute('hidden');
        } else {
          block.setAttribute('hidden', '');
        }
      });
    }

    function updatePlanPrices(variant) {
      // Subscription cards prices sunt computate în Liquid pentru current_variant
      // doar. La schimbare gramaj (250g→1000g) trebuie recalculate din variant
      // JSON (.price + .selling_plan_allocations).

      // One-time card price
      const onetimeEl = hero.querySelector('[data-onetime-price]');
      if (onetimeEl) onetimeEl.textContent = formatMoney(variant.price);

      // Subscription card — strikethrough original = preț variant nou
      const origEl = hero.querySelector('[data-plan-price-original]');
      if (origEl) origEl.textContent = formatMoney(variant.price);

      // Frequency dropdown — update data-plan-price din selling_plan_allocations
      const freqSelect = hero.querySelector('[data-plan-frequency]');
      const allocations = variant.selling_plan_allocations || [];
      if (freqSelect) {
        Array.prototype.forEach.call(freqSelect.options, function (opt) {
          const planId = parseInt(opt.value, 10);
          const alloc = allocations.find(function (a) {
            return a.selling_plan_id === planId;
          });
          if (alloc) {
            opt.dataset.planPrice = alloc.price;
          } else {
            opt.dataset.planPrice = variant.price;
          }
        });
        // Update preț afișat pe card pentru frecvența selectată curent
        const priceDisplay = hero.querySelector('[data-plan-price-display]');
        const selectedOpt = freqSelect.options[freqSelect.selectedIndex];
        if (priceDisplay && selectedOpt && selectedOpt.dataset.planPrice) {
          priceDisplay.textContent = formatMoney(parseInt(selectedOpt.dataset.planPrice, 10));
        }
      }
    }

    function updateAvailability(variant) {
      if (!ctaBtn) return;
      if (variant.available) {
        ctaBtn.disabled = false;
        ctaBtn.removeAttribute('aria-disabled');
        ctaBtn.textContent = ctaBtn.dataset.labelAdd || 'Adaugă în coș';
      } else {
        ctaBtn.disabled = true;
        ctaBtn.setAttribute('aria-disabled', 'true');
        ctaBtn.textContent = ctaBtn.dataset.labelUnavailable || 'Indisponibil';
      }
    }

    function dispatchVariantChange(variant) {
      hero.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: variant }
      }));
    }
  }

  /* -----------------------------------------------------------------
     STICKY BUY BAR — IO observer pe hero buy form parent.
     Când hero form iese din viewport (user scrolled past) → sticky visible.
     Reverse când hero form revine în viewport.
     Plus sync variant.id schimbat în main form → sticky form (variant:change event).
     ----------------------------------------------------------------- */
  function initStickyBuy(hero) {
    const heroForm = hero.querySelector('[data-product-form]');
    const stickyBar = document.querySelector('[data-product-sticky-buy]');
    if (!heroForm || !stickyBar) return;

    if (typeof IntersectionObserver === 'undefined') {
      // Fallback no-IO: sticky always visible (safe default)
      stickyBar.setAttribute('data-visible', 'true');
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // entry.isIntersecting === false → form scrolled out → show sticky
          stickyBar.setAttribute('data-visible', String(!entry.isIntersecting));
        });
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(heroForm);

    // Sync variant.id from main form → sticky form on variant:change event
    hero.addEventListener('variant:change', function (e) {
      const variant = e.detail && e.detail.variant;
      if (!variant) return;
      const stickyVariantInput = stickyBar.querySelector('[data-product-sticky-variant-id]');
      if (stickyVariantInput) stickyVariantInput.value = variant.id;
      const stickyCta = stickyBar.querySelector('.product-sticky-buy__cta');
      if (stickyCta) {
        stickyCta.disabled = !variant.available;
        if (variant.available) {
          stickyCta.removeAttribute('aria-disabled');
        } else {
          stickyCta.setAttribute('aria-disabled', 'true');
        }
      }
    });
  }

  /* -----------------------------------------------------------------
     QUANTITY — increment / decrement with clamp 1..99
     ----------------------------------------------------------------- */
  function initQuantity(hero) {
    const decreaseBtn = hero.querySelector('[data-product-quantity-decrease]');
    const increaseBtn = hero.querySelector('[data-product-quantity-increase]');
    const input = hero.querySelector('[data-product-quantity-input]');
    if (!input) return;

    const MIN = 1;
    const MAX = 99;

    function clamp(v) {
      const n = parseInt(v, 10);
      if (isNaN(n)) return MIN;
      return Math.max(MIN, Math.min(MAX, n));
    }

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', function () {
        input.value = clamp(parseInt(input.value, 10) - 1);
      });
    }
    if (increaseBtn) {
      increaseBtn.addEventListener('click', function () {
        input.value = clamp(parseInt(input.value, 10) + 1);
      });
    }
    input.addEventListener('change', function () {
      input.value = clamp(input.value);
    });
  }
})();
